/* eslint-disable @typescript-eslint/no-unused-vars */
import { supabase } from '../client/supabaseClient';
import axios from 'axios';
const backendUrl = process.env.REACT_APP_BACKEND_URL;
// Define shop interface
export interface Shop {
  id: string;
  name: string;
  location: string;
  institution: string;
  open_hours: string;
  is_online: boolean;
  distance?: number; // Optional distance property when using nearest shops
  address?: string; // Full address of the shop
  monochrome_cost?: number; // Cost for black and white printing per page (A4 default)
  color_cost?: number; // Cost for color printing per page (A4 default)
  pricing?: {
    [paperType: string]: {
      user_monochrome_cost: number;
      user_color_cost: number;
      shop_monochrome_cost: number;
      shop_color_cost: number;
    };
  }; // Pricing for all paper types from Redis
  supported_settings?: {
    [printerId: string]: {
      supportedPrintSettings?: number[];
      [key: string]: any;
    };
  }; // Supported print settings from Redis (capabilities)
  lat?: number; // Latitude for shop location
  lon?: number; // Longitude for shop location
  shop_image_path?: string; // Path to the shop's image
}

/**
 * Fetch shops based on search query
 * Uses multiple data sources for comprehensive results
 */
export const searchShops = async (query: string): Promise<Shop[]> => {
  console.log('shopService: Searching shops with query:', query);
  try {
    const { data, error } = await supabase.schema('api')
                                         .rpc('search_shops', 
    {
      shop_name: query
    });

    console.log('shopService: Search results:', data);

    if (error) {
      console.error('shopService: Error searching shops:', error);
      return [];
    }

    const cachedShops = (data || []).filter((shop: Shop) => shop.is_online);

    // Supplement with database results for complete coverage
    const dbShops = await getOnlineShopsFromDB();
    const filteredDBShops = dbShops.filter((shop: Shop) => 
      shop.name.toLowerCase().includes(query.toLowerCase()) ||
      shop.location?.toLowerCase().includes(query.toLowerCase()) ||
      shop.address?.toLowerCase().includes(query.toLowerCase())
    );

    // Combine results
    const shopMap = new Map<string, Shop>();
    cachedShops.forEach((shop: Shop) => shopMap.set(shop.id, shop));
    filteredDBShops.forEach((shop: Shop) => {
      if (!shopMap.has(shop.id)) {
        shopMap.set(shop.id, shop);
      }
    });

    return Array.from(shopMap.values());
  } catch (error) {
    console.error('shopService: Exception searching shops:', error);
    return [];
  }
};

// /**
//  * Fetch nearest shops based on user coordinates and optional search term
//  */
// export const getNearestShops = async (latitude: number, longitude: number): Promise<Shop[]> => {
//   const { data } = await axios.get(`${backendUrl}/shops`, {
//       params: { lat: latitude, lon: longitude }
//   });
//   console.log ('shopService: Nearest shops results:', data);
//   return data;
// };

// /**
//  * Fetch all shops (used as fallback)
//  */
// export const getAllShops = async (): Promise<Shop[]> => {
//   console.log('shopService: Getting all shops');
//   try {
//     const { data, error } = await supabase.schema('api')
//                                          .rpc('search_shops', {
//       shop_name: '' // Empty string to match all shops
//     });

//     if (error) {
//       console.error('shopService: Error fetching all shops:', error);
//       return [];
//     }

//     // Filter only online shops
//     return (data || []).filter((shop: Shop) => shop.is_online);
//   } catch (error) {
//     console.error('shopService: Exception fetching all shops:', error);
//     return [];
//   }
// };

/**
 * Fetch online shops from database
 * Fallback mechanism when cache is stale
 */
const getOnlineShopsFromDB = async (): Promise<Shop[]> => {
  try {
    const { data, error } = await supabase.schema('api')
                                          .rpc('get_online_shops_from_db');

    if (error) {
      console.error('shopService: Error fetching shops from DB:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('shopService: Exception fetching shops from DB:', error);
    return [];
  }
};

/**
 * Fetch all online shops with redundancy
 * Combines cached and real-time data sources for reliability
 */
export const getAllOnlineShops = async (): Promise<Shop[]> => {
  console.log('shopService: Fetching all online shops...');
  
  try {
    // Fetch from both sources for improved reliability
    const [cachedShops, dbShops] = await Promise.all([
      axios.get(`${backendUrl}/shops/all`)
        .then(response => response.data)
        .catch(error => {
          console.error('shopService: Error fetching cached shops:', error);
          return [];
        }),
      getOnlineShopsFromDB()
    ]);

    // Merge results, preferring cached data for complete shop information
    const shopMap = new Map<string, Shop>();
    
    // Add cached shops first (includes pricing and capabilities)
    cachedShops.forEach((shop: Shop) => {
      shopMap.set(shop.id, shop);
    });
    
    // Add any shops from DB that aren't in cache
    dbShops.forEach((shop: Shop) => {
      if (!shopMap.has(shop.id)) {
        shopMap.set(shop.id, {
          ...shop,
          institution: shop.institution || '',
        });
      }
    });

    const allShops = Array.from(shopMap.values());
    console.log('shopService: Total online shops:', allShops.length);
    
    return allShops;
  } catch (error) {
    console.error('shopService: Error in getAllOnlineShops:', error);
    return [];
  }
};